import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SessionState, MaterializedView, ScenarioDefinition, ScenarioDecision } from '@/types';
import { api, isAdministratorToken } from '@/services/api';
import { useSessionStore } from '@/stores';
import EmailInterface from '@/components/Email/EmailInterface';
import SlackInterface from '@/components/Slack/SlackInterface';
import CalendarView from '@/components/Calendar/CalendarView';
import ContactsView from '@/components/Contacts/ContactsView';
import ContactsCentricPanel from '@/components/Contacts/ContactsCentricPanel';
import PhoneView from '@/components/Phone/PhoneView';
import AccountHistoryView from '@/components/AccountHistory/AccountHistoryView';
import AccountHistoryPanel from '@/components/Dashboard/AccountHistoryPanel';
import DiagnosticsPanel from '@/components/Dashboard/DiagnosticsPanel';
import SessionCompletionModal from '@/components/Dashboard/SessionCompletionModal';
import ActivityFeed, { FeedEntry } from '@/components/Dashboard/ActivityFeed';
import DecisionModal from '@/components/Dashboard/DecisionModal';
import {
    HelpOutline,
    ExitToApp,
    Mail,
    Call,
    Forum,
    AccessTime,
    Warning,
    CalendarMonth,
    People,
    Assignment,
    Favorite,
    Groups,
    AccountBalance,
    OpenInFull
} from '@mui/icons-material';
import { Dialog } from '@mui/material';

interface SimulationDashboardProps {
    session: SessionState;
    materializedView: MaterializedView;
}

const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getInitials = (name: string): string => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Professional headshot photos for NPCs
const NPC_AVATARS: Record<string, string> = {
    // Inbox Crisis
    'jennifer-martinez': 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face',
    'marcus-johnson': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
    'sarah-chen': 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face',
    // The Handover
    'priya-devi': 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face',
    'aanya-joshi': 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face',
    'hugo-lindqvist': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face',
    'greg-whittaker': 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&h=80&fit=crop&crop=face',
    // The Restructure
    'maya-chen': 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=80&h=80&fit=crop&crop=face',
    'tom-bradley': 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face',
    'priya-sharma': 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=face',
    'james-whitfield': 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=80&h=80&fit=crop&crop=face',
    'diana-okafor': 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=80&h=80&fit=crop&crop=face',
    'rajiv-patel': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
    'sarah-lindgren': 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face',
    'marcus-wei': 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&h=80&fit=crop&crop=face',
};

const StitchDashboard: React.FC<SimulationDashboardProps> = ({ session, materializedView }) => {
    // Diagnostics are gated on the viewer's own JWT role, not the session's
    // entitlementSource — an admin may be watching a candidate's live session.
    const edgeHubToken = useSessionStore((s) => s.edgeHubToken);
    const isViewerAdmin = isAdministratorToken(edgeHubToken);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showSlackModal, setShowSlackModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [showContactsModal, setShowContactsModal] = useState(false);
    const [showPhoneModal, setShowPhoneModal] = useState(false);
    const [showAccountHistoryModal, setShowAccountHistoryModal] = useState(false);
    const [showMissionLogModal, setShowMissionLogModal] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showEndSessionConfirm, setShowEndSessionConfirm] = useState(false);
    const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
    const [composeToEmail, setComposeToEmail] = useState<string | undefined>(undefined);
    const [showDecisionHistory, setShowDecisionHistory] = useState(false);
    const [completingSession, setCompletingSession] = useState(false);

    // Scenario data
    const [scenario, setScenario] = useState<ScenarioDefinition | null>(null);

    // Decision state
    const [completedDecisions, setCompletedDecisions] = useState<Map<string, { optionId: string; feedback: string }>>(new Map());
    const [activeDecision, setActiveDecision] = useState<ScenarioDecision | null>(null);
    const [decisionMinimized, setDecisionMinimized] = useState(false);
    const [pendingDecision, setPendingDecision] = useState(false);
    // Track decisions completed via the modal click — these should NOT show the
    // "resolved via comms" feed entry. Without this, the dismiss-modal watcher
    // can't distinguish modal-clicks from comms-resolutions and adds a false
    // comms-resolved message when the option's auto-sent email hits the pipeline.
    const modalCompletedRef = useRef<Set<string>>(new Set());
    const [feedEntries, setFeedEntries] = useState<FeedEntry[]>([]);

    // Server-synced time from MV
    const serverTimeRemaining = materializedView.timeRemainingSeconds ?? session.timeRemaining;
    const serverSimTime = materializedView.simulationTimeSeconds ?? 0;
    const isSessionCompleted = materializedView.sessionStatus === 'Completed' || session.status === 'Completed';

    // Local countdown
    const [localTimeRemaining, setLocalTimeRemaining] = useState(serverTimeRemaining);
    const [localSimTime, setLocalSimTime] = useState(serverSimTime);
    const lastServerTimeRef = useRef(serverTimeRemaining);

    useEffect(() => {
        if (serverTimeRemaining !== lastServerTimeRef.current) {
            setLocalTimeRemaining(serverTimeRemaining);
            setLocalSimTime(serverSimTime);
            lastServerTimeRef.current = serverTimeRemaining;
        }
    }, [serverTimeRemaining, serverSimTime]);

    useEffect(() => {
        if (isSessionCompleted || localTimeRemaining <= 0) return;
        const interval = setInterval(() => {
            setLocalTimeRemaining(prev => Math.max(0, prev - 1));
            setLocalSimTime(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [isSessionCompleted, localTimeRemaining <= 0]);

    // Load scenario on mount
    useEffect(() => {
        const loadScenario = async () => {
            try {
                const data = await api.getScenario(session.scenarioId);
                setScenario(data);
            } catch (err) {
                console.error('Failed to load scenario:', err);
            }
        };
        loadScenario();
    }, [session.scenarioId]);

    // Recover decisions from MV on load + initialize feed
    const feedInitializedRef = useRef(false);

    useEffect(() => {
        if (!scenario) return;

        const recovered = new Map<string, { optionId: string; feedback: string }>();
        const mvDecisions = materializedView.completedDecisions || [];

        for (const cd of mvDecisions) {
            const decision = (scenario?.decisions ?? []).find(d => d.id === cd.decisionId);
            const option = decision?.options.find(o => o.id === cd.optionId);
            recovered.set(cd.decisionId, {
                optionId: cd.optionId,
                feedback: cd.feedback || option?.feedback || ''
            });
        }

        setCompletedDecisions(recovered);

        // Build system + decision entries
        const now = new Date().toISOString();
        const systemEntry: FeedEntry = {
            id: 'system-start',
            type: 'system',
            timestamp: session.startTime || now,
            message: `Simulation started: ${scenario.title}. You have ${Math.floor(scenario.duration / 60)} minutes. Check your inbox — you have unread messages that need attention.`
        };

        const decisionEntries: FeedEntry[] = [];
        for (const cd of mvDecisions) {
            const decision = (scenario?.decisions ?? []).find(d => d.id === cd.decisionId);
            const option = decision?.options.find(o => o.id === cd.optionId);
            if (decision && option) {
                decisionEntries.push({
                    id: `outcome-${cd.decisionId}`,
                    type: 'decision_outcome',
                    timestamp: cd.timestamp || now,
                    prompt: decision.prompt,
                    chosenText: option.text,
                    feedback: cd.feedback || option.feedback
                });
            }
        }

        if (!feedInitializedRef.current) {
            // First run: set system + decision entries (email effect will add emails)
            setFeedEntries([systemEntry, ...decisionEntries]);
            feedInitializedRef.current = true;
        } else {
            // Subsequent runs: merge new decision outcomes without clearing existing entries
            setFeedEntries(prev => {
                const existingIds = new Set(prev.map(e => e.id));
                const newEntries = decisionEntries.filter(e => !existingIds.has(e.id));
                if (newEntries.length === 0) return prev;
                return [...prev, ...newEntries];
            });
        }

        // On recovery: show triggered-but-uncompleted decisions (minimized)
        const triggered = materializedView.triggeredDecisions || [];
        const completedIds = new Set(mvDecisions.map(cd => cd.decisionId));
        const pendingTriggered = triggered.filter(t => !completedIds.has(t.decisionId));

        if (pendingTriggered.length > 0) {
            const nextDecision = (scenario?.decisions ?? []).find(d => d.id === pendingTriggered[0].decisionId);
            if (nextDecision) {
                setActiveDecision(nextDecision);
                setDecisionMinimized(true); // Show minimized on recovery
            }
        }
    }, [scenario]);

    // Keep completedDecisions in sync with MV (comms-resolved decisions, etc.)
    useEffect(() => {
        if (!scenario) return;
        const mvDecisions = materializedView.completedDecisions || [];
        if (mvDecisions.length > completedDecisions.size) {
            const updated = new Map(completedDecisions);
            for (const cd of mvDecisions) {
                if (!updated.has(cd.decisionId)) {
                    const decision = (scenario?.decisions ?? []).find(d => d.id === cd.decisionId);
                    const option = decision?.options.find(o => o.id === cd.optionId);
                    updated.set(cd.decisionId, {
                        optionId: cd.optionId,
                        feedback: cd.feedback || option?.feedback || ''
                    });
                }
            }
            setCompletedDecisions(updated);
        }
    }, [scenario, materializedView.completedDecisions]);

    // Update feed when emails arrive
    const prevEmailCountRef = useRef(0);

    useEffect(() => {
        const inboxEmails = materializedView.inbox?.emails || [];
        const receivedEmails = inboxEmails.filter(e => e.from !== 'player');

        if (receivedEmails.length > prevEmailCountRef.current) {
            const newEmails = receivedEmails.slice(prevEmailCountRef.current);
            setFeedEntries(prev => [
                ...prev,
                ...newEmails.map(e => ({
                    id: `email-recv-${e.id}`,
                    type: 'email_received' as const,
                    timestamp: e.timestamp,
                    from: e.from,
                    subject: e.subject
                }))
            ]);
        }
        prevEmailCountRef.current = receivedEmails.length;
    }, [materializedView.inbox?.emails?.length]);

    // Proactive comms nudge: when NPC frustration > 0.3 and no interaction in 3+ minutes
    const lastNudgeRef = useRef<Record<string, number>>({});

    useEffect(() => {
        if (!scenario) return;
        const rels = materializedView.relationships || {};
        const now = Date.now();

        for (const [npcId, rel] of Object.entries(rels)) {
            if (rel.frustration <= 0.3) continue;

            // Check time since last interaction
            const lastTime = rel.lastInteractionTime ? new Date(rel.lastInteractionTime).getTime() : 0;
            const minutesSinceInteraction = lastTime > 0 ? (now - lastTime) / 60000 : 999;

            if (minutesSinceInteraction < 3) continue;

            // Don't nudge more than once per 3 minutes per NPC
            const lastNudge = lastNudgeRef.current[npcId] ?? 0;
            if (now - lastNudge < 180000) continue;

            const npc = scenario.npcs.find(n => n.id === npcId);
            if (!npc) continue;

            lastNudgeRef.current[npcId] = now;

            // Build actionable nudge — suggest checking their emails or Slack
            const hasUnreadEmail = materializedView.inbox.emails.some(
                e => e.from !== 'player' && !e.isRead && e.from.toLowerCase().includes(npc.name.split(' ')[0].toLowerCase())
            );
            const suggestion = hasUnreadEmail
                ? `Check your inbox — ${npc.name} sent you an email.`
                : `Consider reaching out to ${npc.name} (${npc.role}) via email or Slack.`;

            setFeedEntries(prev => {
                const nudgeId = `nudge-${npcId}-${Math.floor(now / 180000)}`;
                if (prev.some(e => e.id === nudgeId)) return prev;
                return [...prev, {
                    id: nudgeId,
                    type: 'system' as const,
                    timestamp: new Date().toISOString(),
                    message: suggestion
                }];
            });
        }
    }, [scenario, materializedView.relationships, localSimTime]);

    // Watch for escalation level changes and add feed entries
    const prevEscalationRef = useRef<Record<string, number>>({});

    useEffect(() => {
        if (!scenario) return;
        const rels = materializedView.relationships || {};

        for (const [npcId, rel] of Object.entries(rels)) {
            const prevLevel = prevEscalationRef.current[npcId] ?? 0;
            if (rel.escalationLevel > prevLevel) {
                const npc = scenario.npcs.find(n => n.id === npcId);
                const npcName = npc?.name || npcId;

                // Build actionable escalation message
                const action = rel.waitingFor
                    ? `Reply to their message or send them an update about: ${rel.waitingFor}`
                    : `Send ${npcName} a message via email or Slack.`;

                if (rel.escalationLevel === 1) {
                    setFeedEntries(prev => {
                        if (prev.some(e => e.id === `escalation-${npcId}-1`)) return prev;
                        return [...prev, {
                            id: `escalation-${npcId}-1`,
                            type: 'escalation' as const,
                            timestamp: new Date().toISOString(),
                            description: `${npcName} is getting impatient. ${action}`
                        }];
                    });
                } else if (rel.escalationLevel >= 2) {
                    setFeedEntries(prev => {
                        if (prev.some(e => e.id === `escalation-${npcId}-2`)) return prev;
                        return [...prev, {
                            id: `escalation-${npcId}-2`,
                            type: 'escalation' as const,
                            timestamp: new Date().toISOString(),
                            description: `${npcName} is frustrated — they've been waiting too long. ${action}`
                        }];
                    });
                }
            }
            prevEscalationRef.current[npcId] = rel.escalationLevel;
        }
    }, [scenario, materializedView.relationships]);

    // Watch for new triggered decisions from MV (pushed via WebSocket)
    const prevTriggeredRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!scenario) return;

        const triggered = materializedView.triggeredDecisions || [];
        const completedIds = new Set((materializedView.completedDecisions || []).map(cd => cd.decisionId));

        for (const t of triggered) {
            if (completedIds.has(t.decisionId)) continue;
            if (prevTriggeredRef.current.has(t.decisionId)) continue;

            // Check if this decision was resolved via comms (completed same tick as triggered)
            const justCompleted = (materializedView.completedDecisions || []).find(
                cd => cd.decisionId === t.decisionId
            );

            if (justCompleted) {
                // Was this comms-resolved? Check if it appeared simultaneously with trigger
                const decision = (scenario?.decisions ?? []).find(d => d.id === t.decisionId);
                if (decision) {
                    const npc = scenario.npcs.find(n =>
                        decision.storyline && n.role.toLowerCase().includes(decision.storyline)
                    );

                    setFeedEntries(prev => {
                        if (prev.some(e => e.id === `comms-resolved-${t.decisionId}`)) return prev;
                        return [...prev, {
                            id: `comms-resolved-${t.decisionId}`,
                            type: 'comms_resolved_decision' as const,
                            timestamp: new Date().toISOString(),
                            npcName: npc?.name || 'a stakeholder',
                            decisionPrompt: decision.prompt
                        }];
                    });
                }
                continue;
            }

            // New triggered decision that hasn't been completed — show modal
            const decision = (scenario?.decisions ?? []).find(d => d.id === t.decisionId);
            if (decision) {
                setActiveDecision(decision);
                setDecisionMinimized(false);

                setFeedEntries(prev => {
                    if (prev.some(e => e.id === `triggered-${t.decisionId}`)) return prev;
                    return [...prev, {
                        id: `triggered-${t.decisionId}`,
                        type: 'system',
                        timestamp: new Date().toISOString(),
                        message: 'A new decision requires your attention.'
                    }];
                });
            }
        }

        // Check if all decisions are complete
        if ((scenario?.decisions ?? []).length > 0 && completedIds.size >= (scenario?.decisions ?? []).length) {
            setFeedEntries(prev => {
                if (prev.some(e => e.id === 'system-decisions-complete')) return prev;
                return [...prev, {
                    id: 'system-decisions-complete',
                    type: 'system',
                    timestamp: new Date().toISOString(),
                    message: 'All decisions complete. Continue managing your inbox and communications until the session ends.'
                }];
            });
        }

        prevTriggeredRef.current = new Set(triggered.map(t => t.decisionId));
    }, [scenario, materializedView.triggeredDecisions]);

    // Dismiss modal if the active decision was resolved via comms.
    // Check BOTH completedDecisions (immediate resolution) and preResolvedDecisions
    // (pre-resolution stored before the clock tick auto-completes it). Without the
    // preResolved check, the mission log shows "resolved" but the modal stays open
    // for up to 30s until the next clock tick.
    useEffect(() => {
        if (!activeDecision || !scenario) return;

        const completedIds = new Set((materializedView.completedDecisions || []).map(cd => cd.decisionId));
        const preResolvedIds = new Set((materializedView.preResolvedDecisions || []).map(pr => pr.decisionId));

        if (completedIds.has(activeDecision.id) || preResolvedIds.has(activeDecision.id)) {
            // Skip if this decision was completed via the modal click — the outcome
            // feed entry was already added by handleDecisionChoose.
            if (modalCompletedRef.current.has(activeDecision.id)) return;

            // Resolve from completed data if available, otherwise from pre-resolved
            const cd = (materializedView.completedDecisions || []).find(c => c.decisionId === activeDecision.id);
            const pr = (materializedView.preResolvedDecisions || []).find(p => p.decisionId === activeDecision.id);
            const resolvedOptionId = cd?.optionId || pr?.optionId;
            const option = resolvedOptionId ? activeDecision.options.find(o => o.id === resolvedOptionId) : null;

            setFeedEntries(prev => {
                if (prev.some(e => e.id === `comms-resolved-${activeDecision.id}`)) return prev;
                return [...prev, {
                    id: `comms-resolved-${activeDecision.id}`,
                    type: 'comms_resolved_decision' as const,
                    timestamp: new Date().toISOString(),
                    npcName: 'your message',
                    decisionPrompt: activeDecision.prompt
                }];
            });

            if (resolvedOptionId && option) {
                setCompletedDecisions(prev => {
                    const updated = new Map(prev);
                    updated.set(activeDecision.id, {
                        optionId: resolvedOptionId,
                        feedback: cd?.feedback || option.feedback || ''
                    });
                    return updated;
                });
            }

            setActiveDecision(null);
            setDecisionMinimized(false);
        }
    }, [activeDecision, scenario, materializedView.completedDecisions, materializedView.preResolvedDecisions]);

    const handleDecisionChoose = useCallback(async (decisionId: string, optionId: string, timeCostSeconds: number) => {
        if (!scenario || pendingDecision) return;

        setPendingDecision(true);
        // Mark as modal-completed BEFORE the API call. The MV push from the
        // backend can arrive during the await, firing the dismiss-modal watcher.
        // Without this, the watcher sees the completion before modalCompletedRef
        // is set and adds a false "resolved via comms" feed entry.
        modalCompletedRef.current.add(decisionId);
        try {
            const result = await api.makeDecision(session.id, { decisionId, optionId, timeCostSeconds });

            const decision = (scenario?.decisions ?? []).find(d => d.id === decisionId);
            const option = decision?.options.find(o => o.id === optionId);
            const feedback = result.feedback || option?.feedback || '';

            const newCompleted = new Map(completedDecisions);
            newCompleted.set(decisionId, { optionId, feedback });
            setCompletedDecisions(newCompleted);

            // Close the modal — next decision will appear when backend triggers it
            setActiveDecision(null);
            setDecisionMinimized(false);

            // Add outcome to feed
            setFeedEntries(prev => [...prev, {
                id: `outcome-${decisionId}`,
                type: 'decision_outcome',
                timestamp: new Date().toISOString(),
                prompt: decision?.prompt || '',
                chosenText: option?.text || '',
                feedback
            }]);
        } catch (err) {
            console.error('Failed to submit decision:', err);
        } finally {
            setPendingDecision(false);
        }
    }, [scenario, session.id, completedDecisions, pendingDecision]);

    const handleCompleteSession = useCallback(async () => {
        if (completingSession) return;
        setCompletingSession(true);
        try {
            await api.completeSession(session.id);
        } catch (err) {
            console.error('Failed to complete session:', err);
            setCompletingSession(false);
        }
    }, [session.id, completingSession]);

    // Computed data
    const unlistenedVoicemailCount = (materializedView.phone?.voicemails || []).filter(v => !v.isListened).length;
    const timeRemainingSeconds = localTimeRemaining;
    const unreadCount = materializedView.inbox.emails.filter(e => e.to === 'player' && !e.isRead && !e.isArchived).length;
    const totalSlackMessages = Object.values(materializedView.slack?.channels || {}).reduce((t, c) => t + c.messages.length, 0)
        + Object.values(materializedView.slack?.directMessages || {}).reduce((t, msgs) => t + msgs.length, 0);

    const npcs = scenario?.npcs || [];
    const totalDecisions = (scenario?.decisions ?? []).length || 5;
    const completedCount = completedDecisions.size;
    const allDecisionsComplete = totalDecisions > 0 && completedCount >= totalDecisions;

    // Contact engagement tracking for EA-3 scenarios (responses kpiSlot)
    const contactEngagements = (materializedView as any)?.behavior?.contactEngagements || {};
    const engagedContactCount = Object.keys(contactEngagements).length;
    const totalNpcCount = npcs.length || 8;

    const relationships = Object.entries(materializedView.relationships || {});
    const stakeholderHealth = relationships.length > 0
        ? Math.round(relationships.reduce((sum, [, rel]) => sum + ((rel.trust - rel.frustration) * 100), 0) / relationships.length)
        : 50;

    // Per-scenario theme — falls back to legacy slate-blue if scenario has no theme block.
    // We use inline styles with accentHex rather than dynamic Tailwind classes to avoid the
    // CSS purge dropping unused class names.
    const theme = scenario?.theme;
    const accentHex = theme?.accentHex || '#3b82f6';
    const layoutTemplate = theme?.layoutTemplate || 'mission-log';

    // KPI slot definitions: scenarios pick from this set via theme.kpiSlots; default if empty.
    const accountReviewedCount = Object.values(materializedView.accountRecords || {}).filter((r: any) => r.hasBeenViewed).length;
    const accountTotalCount = Object.keys(materializedView.accountRecords || {}).length;
    const kpiSlots = theme?.kpiSlots && theme.kpiSlots.length > 0
        ? theme.kpiSlots
        : [
            { id: 'decisions', label: 'Decisions' },
            { id: 'stakeholders', label: 'Stakeholders' },
            { id: 'timeLeft', label: 'Time Left' },
        ];

    // Generic: take the first scenario deadline rather than looking up "all-hands-meeting" by id.
    // The Handover uses "pipeline-call"; Inbox Crisis uses "all-hands-meeting"; both are the only
    // deadline in their respective scenarios, so [0] is safe.
    const meetingDeadline = materializedView.deadlines?.[0];
    const meetingMinutesLeft = meetingDeadline
        ? Math.max(0, Math.floor((meetingDeadline.atSimulationSeconds - localSimTime) / 60))
        : 99;
    const endgameLabel = theme?.endgameLabel || 'Proceed to All-Hands Meeting';
    const endgameShortLabel = theme?.endgameShortLabel || 'All-hands';

    const emailsReceived = materializedView.inbox.emails.filter(e => e.from !== 'player').length;
    const emailsRead = materializedView.inbox.emails.filter(e => e.from !== 'player' && e.isRead).length;
    const emailsSent = materializedView.inbox.emails.filter(e => e.from === 'player').length;
    const voicemailsListened = (materializedView.phone?.voicemails || []).filter(v => v.isListened).length;
    const totalVoicemails = (materializedView.phone?.voicemails || []).length;

    return (
        <div className="bg-slate-900 font-sans h-screen flex flex-col text-white overflow-hidden">
            <div className="flex-1 flex flex-col w-full relative overflow-hidden">

                {/* HEADER & KPIs */}
                <header className="px-4 pt-4 pb-2 sticky top-0 z-20 md:static md:px-8 md:py-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <div
                                    className="size-12 rounded-full flex items-center justify-center text-white font-bold text-lg border"
                                    style={{ background: `linear-gradient(135deg, ${accentHex}, ${accentHex}cc)`, borderColor: `${accentHex}55` }}
                                >
                                    P
                                </div>
                                <div className="absolute bottom-0 right-0 size-3 bg-green-400 rounded-full border-2 border-slate-900"></div>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    {theme?.headerLabel ? (
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{ backgroundColor: `${accentHex}25`, color: accentHex }}>
                                            {theme.headerLabel}
                                        </span>
                                    ) : (
                                        <h2 className="text-xs text-slate-400 font-medium uppercase tracking-widest">Session {session.id.substring(0, 8)}</h2>
                                    )}
                                    {theme?.headerLabel && (
                                        <span className="text-xs text-slate-500 font-mono">{session.id.substring(0, 8)}</span>
                                    )}
                                </div>
                                <h1 className="text-2xl font-bold text-white leading-none">{scenario?.title || session.scenarioId}</h1>
                                {scenario?.briefing?.role && (
                                    <p className="text-xs text-slate-400 mt-1">{scenario.briefing.role}</p>
                                )}
                            </div>
                        </div>

                        {/* KPI Status Bar — slot-based; scenario.theme.kpiSlots picks which cards to show */}
                        <div className="flex items-center gap-6 bg-slate-800 rounded-full px-6 py-3 border border-slate-700">
                            {kpiSlots.map((slot, idx) => {
                                const isLast = idx === kpiSlots.length - 1;
                                let card: React.ReactNode = null;
                                switch (slot.id) {
                                    case 'decisions':
                                        card = (
                                            <button onClick={() => setShowDecisionHistory(true)} className="flex items-center gap-3 hover:opacity-80 transition-opacity" title="View decision history">
                                                <div style={{ color: accentHex }}><Assignment /></div>
                                                <div className="text-left">
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{slot.label}</p>
                                                    <p className="text-lg font-bold text-white leading-none font-mono">{completedCount}/{totalDecisions}</p>
                                                </div>
                                            </button>
                                        );
                                        break;
                                    case 'stakeholders':
                                        card = (
                                            <div className="flex items-center gap-3">
                                                <div className={stakeholderHealth >= 50 ? 'text-green-400' : stakeholderHealth >= 25 ? 'text-amber-400' : 'text-red-400'}>
                                                    <Favorite />
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{slot.label}</p>
                                                    <p className={`text-lg font-bold leading-none font-mono ${stakeholderHealth >= 50 ? 'text-green-400' : stakeholderHealth >= 25 ? 'text-amber-400' : 'text-red-400'}`}>
                                                        {stakeholderHealth}%
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                        break;
                                    case 'accountReviews':
                                        card = (
                                            <button onClick={() => setShowAccountHistoryModal(true)} className="flex items-center gap-3 hover:opacity-80 transition-opacity" title="Open account history">
                                                <div style={{ color: accentHex }}><AccountBalance /></div>
                                                <div className="text-left">
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{slot.label}</p>
                                                    <p className="text-lg font-bold text-white leading-none font-mono">{accountReviewedCount}/{accountTotalCount}</p>
                                                </div>
                                            </button>
                                        );
                                        break;
                                    case 'timeLeft':
                                        card = (
                                            <div className="flex items-center gap-3">
                                                <div className={timeRemainingSeconds <= 120 ? 'text-red-400' : timeRemainingSeconds <= 300 ? 'text-amber-400' : ''} style={timeRemainingSeconds > 300 ? { color: accentHex } : undefined}>
                                                    <AccessTime />
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{slot.label}</p>
                                                    <p className={`text-lg font-bold leading-none font-mono ${timeRemainingSeconds <= 120 ? 'text-red-400 animate-pulse' : timeRemainingSeconds <= 300 ? 'text-amber-400' : 'text-white'}`}>
                                                        {formatTimeRemaining(timeRemainingSeconds)}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                        break;
                                    case 'inboxRead':
                                        card = (
                                            <button onClick={() => setShowEmailModal(true)} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                                                <div style={{ color: accentHex }}><Mail /></div>
                                                <div className="text-left">
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{slot.label}</p>
                                                    <p className="text-lg font-bold text-white leading-none font-mono">{emailsRead}/{emailsReceived}</p>
                                                </div>
                                            </button>
                                        );
                                        break;
                                    case 'responses':
                                        card = (
                                            <div className="flex items-center gap-3">
                                                <div style={{ color: accentHex }}><Assignment /></div>
                                                <div className="text-left">
                                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{slot.label}</p>
                                                    <p className="text-lg font-bold text-white leading-none font-mono">{engagedContactCount}/{totalNpcCount}</p>
                                                </div>
                                            </div>
                                        );
                                        break;
                                }
                                return (
                                    <React.Fragment key={slot.id}>
                                        {card}
                                        {!isLast && <div className="w-px h-8 bg-slate-700"></div>}
                                    </React.Fragment>
                                );
                            })}
                            {meetingDeadline && !meetingDeadline.passed && meetingMinutesLeft <= 5 && (
                                <>
                                    <div className="w-px h-8 bg-slate-700"></div>
                                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${meetingMinutesLeft <= 2 ? "bg-red-500/20 border border-red-500/40" : "bg-amber-500/20 border border-amber-500/40"}`}>
                                        <Warning fontSize="small" className={meetingMinutesLeft <= 2 ? "text-red-400 animate-pulse" : "text-amber-400"} />
                                        <span className={`text-xs font-bold ${meetingMinutesLeft <= 2 ? "text-red-400" : "text-amber-400"}`}>
                                            {endgameShortLabel} in {meetingMinutesLeft}m
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="hidden md:flex items-center gap-2">
                            <button
                                onClick={() => setShowHelpModal(true)}
                                className="flex items-center justify-center size-10 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white"
                                title="Scenario briefing"
                            >
                                <HelpOutline />
                            </button>
                            <button
                                onClick={() => setShowEndSessionConfirm(true)}
                                className="flex items-center justify-center size-10 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-red-400"
                                title="End session early"
                            >
                                <ExitToApp />
                            </button>
                        </div>
                    </div>
                </header>

                {/* Main Content Grid */}
                <div className="flex-1 overflow-y-auto no-scrollbar pb-32 md:pb-20 px-4 md:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">

                        {/* LEFT: Objective + Activity Feed + Contacts */}
                        <div className="lg:col-span-10 flex flex-col gap-4">
                            {/* Objective Banner */}
                            <section className="h-32 shrink-0">
                                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 relative overflow-hidden h-full flex flex-col justify-center">
                                    <div className="relative z-10 flex items-center justify-between gap-8 h-full">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="flex size-2 rounded-full bg-blue-400 animate-pulse"></span>
                                                <h3 className="text-blue-400 text-[10px] font-bold uppercase tracking-[0.2em]">Current Objective</h3>
                                            </div>
                                            <p className="text-white font-bold text-xl leading-tight tracking-tight">
                                                {scenario?.title || 'Loading scenario...'}
                                            </p>
                                            <p className="text-slate-300 text-xs mt-1">{scenario?.theme?.subtitle ?? 'Navigate your inbox, respond to stakeholders, and make critical decisions.'}</p>
                                        </div>

                                        <div className="flex items-center gap-4 shrink-0">
                                            <div className="w-48 bg-slate-900 rounded-full h-2 border border-slate-700">
                                                <div
                                                    className="bg-blue-500 h-full rounded-full transition-all duration-500"
                                                    style={{ width: `${totalDecisions > 0 ? (completedCount / totalDecisions) * 100 : 0}%` }}
                                                />
                                            </div>
                                            <span className="text-xl font-bold text-blue-400 font-mono">
                                                {totalDecisions > 0 ? Math.round((completedCount / totalDecisions) * 100) : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Prime panel — driven by scenario.theme.layoutTemplate.
                                 - mission-log (default, Inbox Crisis): full Mission Log
                                 - account-focused (Handover): Account History panel + compact Mission Log below
                                 - people-focused (The Restructure EA-3+): Contacts-centric panel */}
                            {layoutTemplate === 'account-focused' ? (
                                <AccountHistoryPanel
                                    session={session}
                                    materializedView={materializedView}
                                    onOpenFull={() => setShowAccountHistoryModal(true)}
                                    accentHex={accentHex}
                                />
                            ) : layoutTemplate === 'people-focused' ? (
                                <div className="flex-1 min-h-[400px]">
                                    <ContactsCentricPanel
                                        session={session}
                                        materializedView={materializedView}
                                        npcs={npcs}
                                        avatarUrls={NPC_AVATARS}
                                        onSendEmail={(email) => {
                                            setComposeToEmail(email);
                                            setShowEmailModal(true);
                                        }}
                                        onOpenSlack={() => {
                                            setShowSlackModal(true);
                                        }}
                                    />
                                </div>
                            ) : (
                                <section className="flex-1 min-h-[400px] bg-slate-800 border border-slate-700 rounded-2xl p-5 flex flex-col relative overflow-hidden">
                                    <div className="flex items-center justify-between mb-4 border-b border-slate-700 pb-2">
                                        <div className="flex items-center gap-2">
                                            <Assignment className="text-blue-400" fontSize="small" />
                                            <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest">Mission Log</h3>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <div className="size-2 rounded-full bg-blue-400 animate-pulse"></div>
                                            <span className="text-[10px] text-slate-500 uppercase">Live</span>
                                        </div>
                                    </div>

                                    <ActivityFeed
                                        entries={feedEntries}
                                    />
                                </section>
                            )}

                            {/* Key Contacts — hidden for people-focused layout (contacts panel is the prime widget) */}
                            <section className={layoutTemplate === 'people-focused' ? 'hidden' : ''}>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Key Contacts</h3>
                                    <button onClick={() => setShowContactsModal(true)} className="text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider hover:underline">View Directory</button>
                                </div>
                                <div className={`grid grid-cols-1 gap-3 ${npcs.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                                    {npcs.slice(0, 4).map((npc) => {
                                        const rel = materializedView.relationships?.[npc.id];
                                        const health = rel ? Math.round((rel.trust - rel.frustration) * 100) : 50;
                                        const statusColor = health >= 50 ? 'bg-green-400' : health >= 25 ? 'bg-amber-400' : 'bg-red-400';
                                        const avatarUrl = NPC_AVATARS[npc.id];

                                        // Qualitative sentiment label — escalation level takes priority
                                        let sentimentReason = '';
                                        const sentimentLabel = !rel ? (sentimentReason = 'no relationship data', 'Neutral') :
                                            rel.escalationLevel >= 2 ? (sentimentReason = `escalationLevel=${rel.escalationLevel} (>=2)`, 'Frustrated') :
                                            rel.escalationLevel >= 1 ? (sentimentReason = `escalationLevel=${rel.escalationLevel} (>=1)`, 'Waiting') :
                                            rel.frustration >= 0.5 ? (sentimentReason = `frustration=${rel.frustration.toFixed(2)} (>=0.5)`, 'Frustrated') :
                                            rel.frustration >= 0.3 ? (sentimentReason = `frustration=${rel.frustration.toFixed(2)} (>=0.3)`, 'Waiting') :
                                            rel.trust >= 0.6 ? (sentimentReason = `trust=${rel.trust.toFixed(2)} (>=0.6)`, 'Positive') :
                                            rel.trust >= 0.4 ? (sentimentReason = `trust=${rel.trust.toFixed(2)} (>=0.4)`, 'Neutral') :
                                            (sentimentReason = `trust=${rel.trust.toFixed(2)} (<0.4)`, 'Strained');
                                        console.log(
                                            `[NPC ${npc.name}] label=${sentimentLabel} reason=${sentimentReason}` +
                                            (rel ? ` | trust=${rel.trust.toFixed(2)} frustration=${rel.frustration.toFixed(2)} escalation=${rel.escalationLevel} waitingFor=${rel.waitingFor || 'none'} lastInteraction=${rel.lastInteractionTime || 'never'}` : '')
                                        );
                                        const sentimentColor = sentimentLabel === 'Positive' ? 'text-green-400' :
                                            sentimentLabel === 'Neutral' ? 'text-slate-400' :
                                            sentimentLabel === 'Waiting' ? 'text-amber-400' :
                                            sentimentLabel === 'Frustrated' ? 'text-red-400' :
                                            sentimentLabel === 'Strained' ? 'text-red-400' : 'text-slate-400';

                                        return (
                                            <div
                                                key={npc.id}
                                                className="flex items-center gap-3 bg-slate-800 border border-slate-700 p-3 rounded-lg hover:border-blue-500/40 transition-all group cursor-pointer relative"
                                                title={rel ? `Trust: ${Math.round(rel.trust * 100)}% | Frustration: ${Math.round(rel.frustration * 100)}%` : 'No interactions yet'}
                                                onClick={() => { setSelectedNpcId(npc.id); setShowContactsModal(true); }}
                                            >
                                                <div className="relative shrink-0">
                                                    {avatarUrl ? (
                                                        <img
                                                            src={avatarUrl}
                                                            alt={npc.name}
                                                            className="size-10 rounded-full object-cover border border-slate-600 group-hover:border-blue-500/50 transition-colors"
                                                        />
                                                    ) : (
                                                        <div className="size-10 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-bold border border-slate-600">
                                                            {getInitials(npc.name)}
                                                        </div>
                                                    )}
                                                    <div className={`absolute bottom-0 right-0 size-2.5 rounded-full border-2 border-slate-800 ${statusColor}`}></div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-bold text-white truncate group-hover:text-blue-400 transition-colors">{npc.name}</h4>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[10px] text-slate-400 truncate uppercase tracking-wide font-medium">{npc.role}</p>
                                                        <span className={`text-[10px] font-bold ${sentimentColor}`}>{sentimentLabel}</span>
                                                    </div>
                                                </div>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Forum fontSize="small" className="text-blue-400" style={{ fontSize: '1rem' }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        </div>

                        {/* RIGHT: Comms Hub + Mission Log (account-focused only) + Session Activity */}
                        <div className="lg:col-span-2 flex flex-col gap-4">
                            <section className={`bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col ${layoutTemplate === 'account-focused' ? '' : 'h-full'}`}>
                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Comms Hub</h3>

                                <div className="flex flex-col gap-2">
                                    <button onClick={() => setShowEmailModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><Mail fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Inbox</span>
                                        </div>
                                        {unreadCount > 0 && (
                                            <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{unreadCount}</span>
                                        )}
                                    </button>

                                    <button onClick={() => setShowPhoneModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><Call fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Calls</span>
                                        </div>
                                        {unlistenedVoicemailCount > 0 && (
                                            <span className="text-[10px] font-bold bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded animate-pulse">{unlistenedVoicemailCount}</span>
                                        )}
                                    </button>

                                    <button onClick={() => setShowSlackModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><Forum fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Channels</span>
                                        </div>
                                        {totalSlackMessages > 0 && (
                                            <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{totalSlackMessages}</span>
                                        )}
                                    </button>

                                    <button onClick={() => setShowCalendarModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><CalendarMonth fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Calendar</span>
                                        </div>
                                        <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{materializedView.calendar?.length ?? 0}</span>
                                    </button>

                                    <button onClick={() => setShowContactsModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><People fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Contacts</span>
                                        </div>
                                    </button>

                                    {/* Account History Comms Hub button — only in mission-log layout. In account-focused
                                        the panel is already in the prime left pane so this would be redundant. */}
                                    {layoutTemplate !== 'account-focused' && Object.keys(materializedView.accountRecords || {}).length > 0 && (
                                        <button onClick={() => setShowAccountHistoryModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-amber-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                            <div className="flex items-center gap-3">
                                                <div className="text-slate-400 group-hover:text-amber-400 transition-colors"><AccountBalance fontSize="small" /></div>
                                                <span className="text-xs font-bold text-white group-hover:text-amber-400 transition-colors">Account History</span>
                                            </div>
                                            {(() => {
                                                const recs = Object.values(materializedView.accountRecords || {});
                                                const unread = recs.filter(r => !r.hasBeenViewed).length;
                                                return unread > 0 ? (
                                                    <span className="text-[10px] font-bold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">{unread}</span>
                                                ) : null;
                                            })()}
                                        </button>
                                    )}
                                </div>

                                {/* Session Activity */}
                                <div className="mt-auto pt-6 border-t border-slate-700">
                                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Session Activity</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Emails Read</span>
                                                <span className="text-white font-bold">{emailsRead}/{emailsReceived}</span>
                                            </div>
                                            <div className="h-1 bg-slate-900 rounded-full overflow-hidden">
                                                <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${emailsReceived > 0 ? (emailsRead / emailsReceived) * 100 : 0}%` }}></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Emails Sent</span>
                                                <span className="text-white font-bold">{emailsSent}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Slack Messages</span>
                                                <span className="text-white font-bold">{totalSlackMessages}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Voicemails</span>
                                                <span className="text-white font-bold">{voicemailsListened}/{totalVoicemails}</span>
                                            </div>
                                            <div className="h-1 bg-slate-900 rounded-full overflow-hidden">
                                                <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${totalVoicemails > 0 ? (voicemailsListened / totalVoicemails) * 100 : 0}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Mission Log card — sibling under Comms Hub, account-focused layout only.
                                Compact preview of the latest entries (one-line summaries); Expand opens
                                the full feed in a modal. */}
                            {layoutTemplate === 'account-focused' && (
                                <section className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Assignment style={{ color: accentHex, fontSize: 16 }} />
                                            <h3 className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accentHex }}>Mission Log</h3>
                                            <div className="size-1.5 rounded-full animate-pulse" style={{ backgroundColor: accentHex }}></div>
                                        </div>
                                        <button
                                            onClick={() => setShowMissionLogModal(true)}
                                            className="text-[10px] font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-wider flex items-center gap-1"
                                            title="Expand mission log"
                                        >
                                            <OpenInFull style={{ fontSize: 12 }} /> Expand
                                        </button>
                                    </div>
                                    <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                                        {feedEntries.length === 0 && (
                                            <li className="text-[11px] text-slate-500 italic text-center py-2">No activity yet.</li>
                                        )}
                                        {feedEntries.slice(-6).reverse().map((entry) => {
                                            const t = new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                                            // Pick the right field per entry type and trim to ~8 words
                                            const raw = (() => {
                                                switch (entry.type) {
                                                    case 'system': return entry.message ?? '';
                                                    case 'decision_outcome': return entry.chosenText ?? entry.prompt ?? 'Decision made';
                                                    case 'email_received': return `${entry.from ?? ''}: ${entry.subject ?? ''}`;
                                                    case 'email_sent': return `→ ${entry.from ?? ''}: ${entry.subject ?? ''}`;
                                                    case 'slack_message': return `#${entry.channel ?? ''} ${entry.content ?? ''}`;
                                                    case 'deadline_warning': return entry.description ?? '';
                                                    case 'escalation': return entry.description ?? '';
                                                    case 'comms_resolved_decision': return `Resolved: ${entry.decisionPrompt ?? ''}`;
                                                    default: return '';
                                                }
                                            })();
                                            const words = raw.trim().split(/\s+/);
                                            const preview = words.slice(0, 8).join(' ') + (words.length > 8 ? '…' : '');
                                            const dotColor = (() => {
                                                switch (entry.type) {
                                                    case 'decision_outcome': return accentHex;
                                                    case 'escalation': return '#f87171';
                                                    case 'deadline_warning': return '#fbbf24';
                                                    case 'comms_resolved_decision': return '#34d399';
                                                    case 'email_received': return '#fbbf24';
                                                    case 'email_sent': return '#60a5fa';
                                                    case 'slack_message': return '#c084fc';
                                                    default: return '#94a3b8';
                                                }
                                            })();
                                            return (
                                                <li key={entry.id} className="flex items-center gap-2 text-[11px] leading-tight">
                                                    <span className="size-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                                                    <span className="font-mono text-slate-500 shrink-0">{t}</span>
                                                    <span className="text-slate-300 truncate">{preview}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </section>
                            )}

                            {/* Diagnostics panel — admin only. Shows the diagnostic log from
                                the MV so admins can see what the AI pipeline is doing. */}
                            {isViewerAdmin && (
                                <DiagnosticsPanel entries={materializedView.diagnosticLog ?? []} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Decision Modal (full or minimized pill) */}
            {activeDecision && !decisionMinimized && (
                <DecisionModal
                    decision={activeDecision}
                    onChoose={(optionId, timeCost) => {
                        handleDecisionChoose(activeDecision.id, optionId, timeCost);
                    }}
                    onMinimize={() => setDecisionMinimized(true)}
                    disabled={pendingDecision}
                />
            )}
            {activeDecision && decisionMinimized && (
                <button
                    onClick={() => setDecisionMinimized(false)}
                    className="fixed bottom-6 right-6 z-[1400] flex items-center gap-2.5 bg-slate-800 border border-blue-500/40 rounded-full px-4 py-2.5 shadow-lg shadow-blue-500/10 hover:border-blue-500/60 hover:bg-slate-700 transition-all group"
                >
                    <span className="relative flex size-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full size-2.5 bg-blue-400"></span>
                    </span>
                    <span className="text-xs font-bold text-blue-400 group-hover:text-blue-300">Decision pending</span>
                </button>
            )}

            {/* Email Modal — close button uses absolute (not fixed) so it doesn't
                 overlap the Reply/Flag/Archive action buttons in the email header. */}
            <Dialog open={showEmailModal} onClose={() => { setShowEmailModal(false); setComposeToEmail(undefined); }} maxWidth="xl" fullWidth
                PaperProps={{ sx: { height: '90vh', bgcolor: '#1e293b', position: 'relative' } }}>
                <EmailInterface session={session} materializedView={materializedView} scenario={scenario} initialComposeTo={composeToEmail} />
                <button onClick={() => { setShowEmailModal(false); setComposeToEmail(undefined); }}
                    className="absolute top-3 right-3 z-[60] p-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg transition-colors"
                    title="Close Email">
                    <span className="text-white text-xs font-bold">✕</span>
                </button>
            </Dialog>

            {/* Slack Modal */}
            <Dialog open={showSlackModal} onClose={() => setShowSlackModal(false)} maxWidth="xl" fullWidth
                PaperProps={{ sx: { height: '90vh', bgcolor: '#1e293b' } }}>
                <SlackInterface session={session} materializedView={materializedView} scenario={scenario} />
                <button onClick={() => setShowSlackModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Slack">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Calendar Modal */}
            <Dialog open={showCalendarModal} onClose={() => setShowCalendarModal(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { height: '80vh', bgcolor: '#1e293b' } }}>
                <CalendarView materializedView={materializedView} />
                <button onClick={() => setShowCalendarModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Calendar">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Contacts Modal */}
            <Dialog open={showContactsModal} onClose={() => { setShowContactsModal(false); setSelectedNpcId(null); }} maxWidth="sm" fullWidth
                PaperProps={{ sx: { height: '70vh', bgcolor: '#1e293b', overflow: 'visible' } }}>
                <ContactsView session={session} materializedView={materializedView} scenario={scenario}
                    initialSelectedNpcId={selectedNpcId}
                    onSendEmail={(email) => { setShowContactsModal(false); setSelectedNpcId(null); setComposeToEmail(email); setShowEmailModal(true); }} />
                <button onClick={() => { setShowContactsModal(false); setSelectedNpcId(null); }}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Contacts">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Phone Modal */}
            <Dialog open={showPhoneModal} onClose={() => setShowPhoneModal(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { height: '70vh', bgcolor: '#1e293b' } }}>
                <PhoneView session={session} materializedView={materializedView}
                    onReplyByEmail={(callerId) => {
                        const npc = scenario?.npcs?.find(n => n.id === callerId);
                        const email = npc?.email || callerId;
                        setShowPhoneModal(false); setComposeToEmail(email); setShowEmailModal(true);
                    }} />
                <button onClick={() => setShowPhoneModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Phone">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* End Session Confirmation */}
            <Dialog open={showEndSessionConfirm} onClose={() => setShowEndSessionConfirm(false)}
                PaperProps={{ sx: { bgcolor: '#1e293b', borderRadius: 3, maxWidth: 420 } }}>
                <div className="p-6 text-center">
                    <div className="mx-auto mb-4 size-14 rounded-full bg-red-500/15 flex items-center justify-center">
                        <Warning className="text-red-400" style={{ fontSize: '1.75rem' }} />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">End Session Early?</h2>
                    <p className="text-slate-300 text-sm mb-1">
                        You have <span className="font-bold text-white">{formatTimeRemaining(timeRemainingSeconds)}</span> remaining
                        and <span className="font-bold text-white">{(scenario?.decisions ?? []).length > 0 ? `${completedCount}/${totalDecisions} decisions` : `${engagedContactCount}/${totalNpcCount} contacts`}</span> completed.
                    </p>
                    <p className="text-slate-400 text-xs mb-6">
                        Ending early means your results will be based on incomplete data.
                        Unanswered decisions and unread content will affect your scores.
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => setShowEndSessionConfirm(false)}
                            className="px-5 py-2.5 rounded-lg text-sm font-bold text-slate-300 bg-slate-700 hover:bg-slate-600 transition-colors"
                        >
                            Keep Going
                        </button>
                        <button
                            onClick={() => { setShowEndSessionConfirm(false); handleCompleteSession(); }}
                            disabled={completingSession}
                            className="px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-red-500 hover:bg-red-400 disabled:opacity-50 transition-colors"
                        >
                            {completingSession ? 'Ending...' : 'End Session'}
                        </button>
                    </div>
                </div>
            </Dialog>

            {/* Help / Briefing Modal — shows the scenario briefing so the player can
                 re-read their role, objectives, and tips at any time during the sim. */}
            <Dialog open={showHelpModal} onClose={() => setShowHelpModal(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { bgcolor: '#1e293b', maxHeight: '80vh' } }}>
                <div className="p-6 text-white">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <HelpOutline style={{ color: accentHex }} />
                            <h2 className="text-lg font-bold">Scenario Briefing</h2>
                        </div>
                        <button onClick={() => setShowHelpModal(false)}
                            className="p-1 hover:bg-white/10 rounded-lg transition-colors">
                            <span className="text-slate-300 text-sm font-bold">✕</span>
                        </button>
                    </div>

                    {scenario?.briefing ? (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Your Role</h3>
                                <p className="text-white font-medium">{scenario.briefing.role}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Situation</h3>
                                <p className="text-slate-200 text-sm leading-relaxed">{scenario.briefing.context}</p>
                            </div>
                            {scenario.briefing.objectives.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Objectives</h3>
                                    <ol className="space-y-1.5">
                                        {scenario.briefing.objectives.map((obj, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                                                <span className="text-xs font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                                                    style={{ backgroundColor: `${accentHex}25`, color: accentHex }}>{i + 1}</span>
                                                {obj}
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                            {scenario.briefing.tips.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Tips</h3>
                                    <ul className="space-y-1.5">
                                        {scenario.briefing.tips.map((tip, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                                <span className="text-slate-500 shrink-0">•</span>
                                                {tip}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-slate-400 text-sm">No briefing available for this scenario.</p>
                    )}
                </div>
            </Dialog>

            {/* Mission Log Modal — full activity feed for layouts where it's compacted in the rail */}
            <Dialog open={showMissionLogModal} onClose={() => setShowMissionLogModal(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { height: '80vh', bgcolor: '#1e293b' } }}>
                <div className="flex flex-col h-full">
                    <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
                        <Assignment style={{ color: accentHex }} />
                        <div>
                            <h2 className="text-lg font-bold text-white">Mission Log</h2>
                            <p className="text-xs text-slate-400">Full activity feed</p>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            <div className="size-2 rounded-full animate-pulse" style={{ backgroundColor: accentHex }}></div>
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Live</span>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6">
                        <ActivityFeed entries={feedEntries} />
                    </div>
                </div>
                <button onClick={() => setShowMissionLogModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Mission Log">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Account History Modal — slate theme matching other comms surfaces, amber accent for identity */}
            <Dialog open={showAccountHistoryModal} onClose={() => setShowAccountHistoryModal(false)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { height: '85vh', bgcolor: '#1e293b' } }}>
                <AccountHistoryView session={session} materializedView={materializedView} />
                <button onClick={() => setShowAccountHistoryModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Account History">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Decision History Modal */}
            <Dialog open={showDecisionHistory} onClose={() => setShowDecisionHistory(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { maxHeight: '80vh', bgcolor: '#1e293b' } }}>
                <div className="p-6 text-white">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Assignment className="text-blue-400" />
                            <h2 className="text-xl font-bold">Decision History</h2>
                            <span className="text-sm text-slate-400 font-mono">{completedCount}/{totalDecisions}</span>
                        </div>
                        <button onClick={() => setShowDecisionHistory(false)}
                            className="p-1 hover:bg-white/10 rounded-lg transition-colors">
                            <span className="text-slate-300 text-sm font-bold">✕</span>
                        </button>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full h-2 bg-slate-700 rounded-full mb-6 overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${totalDecisions > 0 ? (completedCount / totalDecisions) * 100 : 0}%` }} />
                    </div>

                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                        {(scenario?.decisions ?? []).map((decision) => {
                            const completed = completedDecisions.get(decision.id);
                            const triggered = (materializedView.triggeredDecisions || []).some(t => t.decisionId === decision.id);
                            const commsResolved = completed && (materializedView.completedDecisions || []).find(
                                cd => cd.decisionId === decision.id
                            );
                            const isCommsResolved = commsResolved && feedEntries.some(e => e.id === `comms-resolved-${decision.id}`);
                            const chosenOption = completed ? decision.options.find(o => o.id === completed.optionId) : null;

                            return (
                                <div key={decision.id}
                                    className={`border rounded-xl p-4 ${
                                        completed ? 'border-green-500/30 bg-green-500/5' :
                                        triggered ? 'border-amber-500/30 bg-amber-500/5' :
                                        'border-slate-700 bg-slate-800/50'
                                    }`}>
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <p className="text-sm font-medium text-slate-200 leading-relaxed flex-1">
                                            {(completed || triggered) ? decision.prompt : 'Decision not yet available'}
                                        </p>
                                        {completed ? (
                                            <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded ${
                                                isCommsResolved ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                                            }`}>
                                                {isCommsResolved ? 'Resolved via comms' : 'Completed'}
                                            </span>
                                        ) : triggered ? (
                                            <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded bg-amber-500/20 text-amber-400">Active</span>
                                        ) : (
                                            <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded bg-slate-700 text-slate-400">Upcoming</span>
                                        )}
                                    </div>
                                    {completed && chosenOption && (
                                        <div className="mt-3 pl-3 border-l-2 border-green-500/30">
                                            <p className="text-xs text-green-400 font-bold mb-1">Your choice: {chosenOption.text}</p>
                                            {completed.feedback && (
                                                <p className="text-xs text-slate-400 leading-relaxed">{completed.feedback}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Dialog>

            {/* Proceed to endgame button — shown when all decisions complete. Label comes from
                scenario.theme.endgameLabel (defaults to "Proceed to All-Hands Meeting" for inbox-crisis). */}
            {allDecisionsComplete && !isSessionCompleted && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1400] animate-fade-in">
                    <button
                        onClick={handleCompleteSession}
                        disabled={completingSession}
                        className="flex items-center gap-3 disabled:opacity-50 text-white font-bold px-8 py-4 rounded-2xl shadow-2xl transition-all hover:scale-105"
                        style={{ backgroundColor: accentHex, boxShadow: `0 24px 48px -12px ${accentHex}55` }}
                    >
                        <Groups style={{ fontSize: '1.5rem' }} />
                        <span>{completingSession ? 'Wrapping up...' : endgameLabel}</span>
                    </button>
                </div>
            )}

            {/* Session Completion Modal */}
            <SessionCompletionModal isOpen={isSessionCompleted} sessionId={session.id} playerInitiated={allDecisionsComplete} endgameLabel={theme?.endgameLabel} />
        </div>
    );
};

export default StitchDashboard;
